import { apiClient } from '@/utils/api';

// Notification types - extensible for future types
export type NotificationType = 
  | 'challenge'           // Someone challenged you
  | 'result'              // Match result recorded
  | 'ranking'             // Ranking change
  | 'reminder'            // Scheduled reminders
  | 'invite'              // Group/league invitation
  | 'score_submission'    // Score needs confirmation
  | 'score_confirmed'     // Score was confirmed
  | 'score_disputed'      // Score was disputed
  | 'member_joined'       // New member joined group
  | 'member_left'         // Member left group
  | 'admin_message'       // Message from admin
  | 'system';             // System notifications

// Action types for follow-through functionality
export type NotificationActionType = 
  | 'navigate'            // Navigate to a URL/route
  | 'accept_score'        // Accept a score submission
  | 'dispute_score'       // Dispute a score submission  
  | 'accept_invite'       // Accept an invitation
  | 'decline_invite'      // Decline an invitation
  | 'view_match'          // View match details
  | 'view_league'         // View league details
  | 'confirm';            // Generic confirmation action

// Action configuration for notifications
export interface NotificationAction {
  type: NotificationActionType;
  label: string;
  url?: string;           // For navigation actions
  payload?: Record<string, unknown>; // Additional data for the action
  primary?: boolean;      // Is this the primary action?
}

// Notification scope
export type NotificationScope = 'personal' | 'group';

// Notification structure
export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
  expiresAt?: string;     // Optional expiration for time-sensitive notifications
  
  // Scope: 'personal' for user-specific, 'group' for group-wide
  scope: NotificationScope;
  groupId?: string;        // Set when scope is 'group'
  groupName?: string;      // Display name of the group (for group notifications)
  
  // Optional context for the notification
  leagueId?: string;
  seasonId?: string;
  bracketId?: string;
  matchId?: string;
  fromUserId?: string;    // Who triggered the notification
  fromUserName?: string;  // Display name of who triggered it
  
  // Actions available for this notification
  actions?: NotificationAction[];
  
  // Generic metadata for extensibility
  metadata?: Record<string, unknown>;
}

// Response types
export interface NotificationsResponse {
  data: Notification[];
  meta: {
    total: number;
    unreadCount: number;
    lastUpdated: string;
  };
}

export interface UnreadCountResponse {
  data: {
    unreadCount: number;
    lastUpdated: string;
  };
}

export interface NotificationResponse {
  data: Notification;
}

export interface MarkAllReadResponse {
  data: {
    updated: number;
  };
}

export interface DismissResponse {
  data: {
    deleted?: boolean;
    dismissed?: boolean;
  };
}

export interface ActionExecutionResponse {
  data: {
    action: NotificationAction;
    notification: Notification;
    executed: boolean;
  };
}

export interface ListNotificationsOptions {
  unread?: boolean;
  limit?: number;
  scope?: NotificationScope;
}

export const notificationService = {
  /**
   * List all notifications for the current user (personal + group)
   */
  list: async (options?: ListNotificationsOptions): Promise<NotificationsResponse> => {
    const params = new URLSearchParams();
    if (options?.unread) params.set('unread', 'true');
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.scope) params.set('scope', options.scope);
    
    const queryString = params.toString();
    const url = queryString ? `/notifications?${queryString}` : '/notifications';
    
    return apiClient.authGet<NotificationsResponse>(url);
  },

  /**
   * Get unread count only (lightweight for polling)
   */
  getUnreadCount: async (): Promise<UnreadCountResponse> => {
    return apiClient.authGet<UnreadCountResponse>('/notifications/unread-count');
  },

  /**
   * Mark a single notification as read
   */
  markAsRead: async (
    notificationId: string, 
    options?: { scope?: NotificationScope; groupId?: string }
  ): Promise<NotificationResponse> => {
    const params = new URLSearchParams();
    if (options?.scope) params.set('scope', options.scope);
    if (options?.groupId) params.set('groupId', options.groupId);
    
    const queryString = params.toString();
    const url = queryString 
      ? `/notifications/${notificationId}/read?${queryString}` 
      : `/notifications/${notificationId}/read`;
    
    return apiClient.authPatch<NotificationResponse>(url, {});
  },

  /**
   * Mark all notifications as read
   */
  markAllAsRead: async (): Promise<MarkAllReadResponse> => {
    return apiClient.authPost<MarkAllReadResponse>('/notifications/mark-all-read', {});
  },

  /**
   * Dismiss/delete a notification
   */
  dismiss: async (notificationId: string): Promise<DismissResponse> => {
    return apiClient.authDelete<DismissResponse>(`/notifications/${notificationId}`);
  },

  /**
   * Execute an action on a notification
   */
  executeAction: async (
    notificationId: string, 
    actionType: NotificationActionType
  ): Promise<ActionExecutionResponse> => {
    return apiClient.authPost<ActionExecutionResponse>(
      `/notifications/${notificationId}/action`, 
      { actionType }
    );
  },
};
